import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import orderBy from 'lodash.orderby';
import { toLocationOption } from '@helpers/locations';
import { useTranslation } from './useTranslation';

// Contacts are visible account-wide, so their location vocabulary is the whole account — not the
// user's assigned-locations scope. This reads the account-wide list loaded by `contactsActions.getLocations`
// (state.contacts.locations) rather than the scoped `account.companies`. Method names mirror
// `useLocations` so the two are mentally interchangeable.
export const useContactLocations = () => {
    const locations = useSelector(state => state.contacts?.locations || []);
    const { t } = useTranslation();
    const noneGroup = `(${t('common.none')})`;

    const getLocationsOptions = useCallback(
        () =>
            orderBy(
                locations.map(location => toLocationOption(location, noneGroup)),
                ['group'],
                ['desc'],
            ),
        [locations, noneGroup],
    );

    const mapSelectedLocationsToFormValue = useCallback(
        selectedIds => {
            if (!selectedIds?.length) return [];
            const byId = new Map(locations.map(location => [location.companyId, location]));
            // Never drop an id the lookup hasn't resolved (e.g. it loaded stale or a location was
            // added mid-session) — keep it with a fallback label so an edit can't silently truncate
            // the contact's assignments on save.
            return selectedIds.map(id => {
                const location = byId.get(id);
                return location ? toLocationOption(location, noneGroup) : { id, label: String(id), group: noneGroup };
            });
        },
        [locations, noneGroup],
    );

    return { contactLocations: locations, getLocationsOptions, mapSelectedLocationsToFormValue };
};
